/**
 * useWavePulse — the single hook that owns Wave Pulse for the entire app.
 *
 * Why this hook exists
 * --------------------
 * Wave Pulse used to render in 8 places, computed from 3 different
 * sources (static `waves.wave_pulse` field, live engine via wave_control,
 * waveMetrics derivation). They drifted. This hook is the only thing
 * any component should ever call to read Wave Pulse.
 *
 * Contract (mirrors backend response shape, see
 * /app/backend/routes/wave_pulse_contract.py):
 *
 *   { pulse_score, delta_since_launch, computed_at, status,
 *     composites: { engagement, reach, diversity, velocity, approval },
 *     headline_translation, flight_deck, tier }
 *
 * Live waves auto-refresh every 30s. Past waves are read once. Projected
 * (draft) waves are read once. Callers can force a refresh via `refetch`.
 *
 * Usage:
 *   const { pulse, composites, headline, flightDeck, tier, status,
 *           isLive, isLoading, error, refetch } = useWavePulse(waveId);
 *
 * Defaults to `null` when waveId is null/undefined — components should
 * branch on `isLoading || !pulse` rather than checking individual fields.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const LIVE_REFRESH_MS = 30_000;

export default function useWavePulse(waveId, opts = {}) {
  const { enabled = true } = opts;
  const [data, setData]       = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const tickRef = useRef(null);
  const aliveRef = useRef(true);

  const fetchOnce = useCallback(async () => {
    if (!waveId) return;
    try {
      setError(null);
      const res = await axios.get(
        `${API}/api/brand/waves/${encodeURIComponent(waveId)}/pulse`,
        { withCredentials: true },
      );
      if (aliveRef.current) setData(res.data);
    } catch (e) {
      if (aliveRef.current) setError(e?.response?.data?.detail || 'Could not load Wave Pulse');
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [waveId]);

  useEffect(() => {
    aliveRef.current = true;
    if (!waveId || !enabled) {
      setData(null);
      setLoading(false);
      return () => { aliveRef.current = false; };
    }
    setLoading(true);
    fetchOnce();
    return () => {
      aliveRef.current = false;
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [waveId, enabled, fetchOnce]);

  // Live auto-refresh — only for waves in `live` status
  useEffect(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (!data || data.status !== 'live' || !enabled) return;
    tickRef.current = setInterval(fetchOnce, LIVE_REFRESH_MS);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [data, enabled, fetchOnce]);

  return {
    pulse:        data?.pulse_score ?? null,
    delta:        data?.delta_since_launch ?? 0,
    computedAt:   data?.computed_at ?? null,
    status:       data?.status ?? 'projected',
    isLive:       data?.status === 'live',
    composites:   data?.composites ?? null,
    headline:     data?.headline_translation ?? '',
    flightDeck:   data?.flight_deck ?? [],
    tier:         data?.tier ?? 'starter',
    isLoading,
    error,
    refetch:      fetchOnce,
    raw:          data,
  };
}
